import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Card, Input } from 'antd';
import type { FilterValue, TablePaginationConfig } from 'antd/es/table/interface';
import { useParams } from 'react-router';

import {
  type IParticipant,
  type IParticipantsStat,
  useLazyGetSchoolParticipantsQuery,
} from '@/api/endpoints/schools/schoolParticipants';
import type { EParticipantsStatus } from '@/shared/consts';
import { debounce } from '@/shared/helpers';
import { useApiRequest } from '@/shared/hooks';

import { ParticipantsList } from '../ParticipantsList/ParticipantsList';
import { ParticipantsStat } from '../ParticipantsStat/ParticipantsStat';

import { ParticipantsContext } from './ParticipantsContext';
import styles from './ParticipantsSection.module.scss';

export const ParticipantsSection = () => {
  const [participantsTableFilters, setParticipantsTableFilters] = useState<Record<string, FilterValue | null>>({});
  const [participantsTablePagination, setParticipantsTablePagination] = useState<TablePaginationConfig>({
    pageSize: 10,
    current: 1,
    total: 0,
  });
  const [participantsTableSearchValue, setParticipantsTableSearchValue] = useState('');
  const [participantsTableData, setParticipantsTableData] = useState<IParticipant[]>();
  const [participantsStat, setParticipantsStat] = useState<IParticipantsStat>();

  const { id: schoolId } = useParams();

  const [getParticipantsData] = useLazyGetSchoolParticipantsQuery();

  const sendApiRequest = useApiRequest();

  const onSearchHandler = debounce({
    callback: (e: ChangeEvent<HTMLInputElement>) => {
      setParticipantsTablePagination((prev) => ({ ...prev, current: 1 }));
      setParticipantsTableSearchValue(e.target.value);
    },
  });

  useEffect(() => {
    const fetchParticipantsData = async () => {
      await sendApiRequest({
        apiRequestCallback: () =>
          getParticipantsData({
            schoolId: Number(schoolId),
            page: participantsTablePagination.current,
            perPage: participantsTablePagination.pageSize,
            statusFilter: participantsTableFilters.status as EParticipantsStatus[],
            nameOrEmailSearchString: participantsTableSearchValue,
          }),
        successCallback: (response) => {
          const {
            data,
            meta: {
              pagination: { total_count: totalPages },
              stats,
            },
          } = response;

          setParticipantsTableData(data);
          setParticipantsTablePagination((prev) => ({
            ...prev,
            total: totalPages,
          }));
          setParticipantsStat(stats);
        },
      });
    };

    fetchParticipantsData();
  }, [
    participantsTableFilters,
    participantsTableSearchValue,
    participantsTablePagination?.current,
    participantsTablePagination?.pageSize,
  ]);

  if (participantsTableData && participantsStat) {
    return (
      <ParticipantsContext.Provider
        value={{
          filters: participantsTableFilters,
          setFilters: setParticipantsTableFilters,
          pagination: participantsTablePagination,
          setPagination: setParticipantsTablePagination,
          participantsTableData,
          setParticipantsTableData,
          searchValue: participantsTableSearchValue,
          participantsStat,
        }}
      >
        <div className={styles.wrapper}>
          <ParticipantsStat />
          <Card>
            <Input
              placeholder="Поиск по ФИО или email..."
              size="large"
              prefix={<SearchOutlined />}
              onChange={onSearchHandler}
            />
          </Card>
          <ParticipantsList />
        </div>
      </ParticipantsContext.Provider>
    );
  }
};
